import * as React from 'react';
import { TCartReview } from './CartReview.types';
import './CartReview.styled.scss';
import { IconButton, Button, Typography, Divider } from '@mui/material';
import { Delete, Remove, Add } from '@mui/icons-material';

export const CartReview: TCartReview = ({
  items,
  onQuantityChange,
  onRemoveItem,
  onContinue,
}) => {
  const subtotal = React.useMemo(() => {
    return items.reduce(
      (sum, item) => sum + item.pricePerItem * item.quantity,
      0
    );
  }, [items]);

  const handleQuantityChange = (itemId: string, delta: number) => {
    onQuantityChange(itemId, delta);
  };

  const handleRemove = (itemId: string) => {
    onRemoveItem(itemId);
  };

  if (items.length === 0) {
    return (
      <div className="cart-review">
        <div className="cart-review__empty">
          <Typography variant="h6">Your cart is empty</Typography>
          <Typography variant="body2" color="textSecondary">
            Add items to your cart to continue
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-review">
      <div className="cart-review__header">
        <Typography variant="h5" className="cart-review__title">
          Review Your Order
        </Typography>
      </div>

      <div className="cart-review__items">
        {items.map((item) => (
          <div key={item.id} className="cart-review__item">
            <div className="cart-review__item-image">
              <img src={item.mainImage} alt={item.title} />
            </div>
            <div className="cart-review__item-details">
              <Typography variant="h6" className="cart-review__item-title">
                {item.title}
              </Typography>
              {Object.keys(item.values).length > 0 && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="cart-review__item-description"
                >
                  {Object.entries(item.values)
                    .map(([key, value]) => `${key}: ${value}`)
                    .join(' / ')}
                </Typography>
              )}
              <div className="cart-review__item-footer">
                <div className="cart-review__item-controls">
                  <div className="cart-review__quantity-control">
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange(item.id, -1)}
                      className="cart-review__quantity-button"
                    >
                      <Remove fontSize="small" />
                    </IconButton>
                    <span className="cart-review__quantity-value">
                      {item.quantity}
                    </span>
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange(item.id, 1)}
                      className="cart-review__quantity-button"
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </div>
                  <IconButton
                    onClick={() => handleRemove(item.id)}
                    className="cart-review__delete-button"
                    aria-label="remove item"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </div>
                <div className="cart-review__item-price">
                  <Typography variant="h6">
                    {(item.pricePerItem * item.quantity).toFixed(2)} zł
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Divider className="cart-review__divider" />

      <div className="cart-review__summary">
        <div className="cart-review__summary-row">
          <Typography variant="body1">Subtotal</Typography>
          <Typography variant="h6">{subtotal.toFixed(2)} zł</Typography>
        </div>
        <div className="cart-review__summary-row">
          <Typography variant="body2" color="textSecondary">
            Shipping (calculated in next step)
          </Typography>
        </div>
      </div>

      <div className="cart-review__actions">
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={onContinue}
          className="cart-review__continue-button"
        >
          Continue to Shipping
        </Button>
      </div>
    </div>
  );
};
